/**
 * Account contact verification helpers.
 *
 * Supabase Auth is the source of truth for verified email / phone. The
 * profiles table mirrors contact fields for product queries, but routing
 * should look at auth.users so metadata-only phone values do not count as
 * verified phone ownership.
 */
import type { UserProfile } from '../types/auth.types'

type AuthContactUser = {
  email?: string | null
  email_confirmed_at?: string | null
  phone?: string | null
  phone_confirmed_at?: string | null
}

export type ContactVerificationRequirement = 'email' | 'phone' | null

const INTERNAL_EMAIL_SUFFIXES = [
  '@phone.tago.app',
  '@anon.tago.app',
  '@deleted.tago.app',
  '@tago.local',
]

export function isInternalContactEmail(email: string | null | undefined): boolean {
  const normalized = email?.trim().toLowerCase()
  if (!normalized) return true
  return INTERNAL_EMAIL_SUFFIXES.some((suffix) => normalized.endsWith(suffix))
}

export function realContactEmail(
  user: AuthContactUser | null | undefined,
  _profile: Pick<UserProfile, 'email'> | null | undefined,
): string | null {
  const userEmail = user?.email?.trim() || null
  if (userEmail && !isInternalContactEmail(userEmail)) return userEmail

  return null
}

export function hasVerifiedAuthPhone(user: AuthContactUser | null | undefined): boolean {
  return Boolean(user?.phone?.trim() && user?.phone_confirmed_at)
}

/**
 * Un correo REAL y además CONFIRMADO.
 *
 * `realContactEmail` solo dice que la dirección no es una de las internas. Que
 * exista no prueba que sea de quien la escribió: eso lo prueba haber metido el
 * código que llegó ahí. Son dos preguntas distintas y la puerta necesita la
 * segunda.
 */
export function hasVerifiedAuthEmail(
  user: AuthContactUser | null | undefined,
  profile: Pick<UserProfile, 'email'> | null | undefined,
): boolean {
  return Boolean(realContactEmail(user, profile) && user?.email_confirmed_at)
}

/**
 * Lo que DETIENE la entrada: no tener NINGÚN contacto verificado.
 *
 * La regla es simétrica y esa simetría es el punto. Te registrás con teléfono,
 * metés el código, entrás — y el correo queda pendiente. Te registrás con
 * correo, metés el código, entrás — y el teléfono queda pendiente. Después
 * entrás con cualquiera de los dos.
 *
 * De dónde viene esto: la puerta exigía los DOS contactos, y como un registro
 * por teléfono siempre arrastra un correo interno `@phone.tago.app`, la
 * condición del correo se cumplía siempre en falso. El resultado era que
 * alguien terminaba el OTP, su cuenta quedaba creada, y la pantalla siguiente le
 * pedía un correo — que desde afuera no se lee como un segundo paso sino como
 * que lo devolvieron a registrarse. Y al reintentar, su número ya estaba tomado
 * por la cuenta que él mismo acababa de crear un minuto antes.
 *
 * Lo que se verifica es la vía por la que la persona entró. Lo que falta no
 * desaparece: se convierte en un paso del centro de verificación. Ver
 * `getPendingContactVerification`.
 *
 * OJO, Y ESTO IMPORTA: esto gobierna la ENTRADA, no lo que se puede hacer
 * adentro. Un teléfono verificado sigue siendo indispensable antes de reservar
 * — es el contacto con el que un anfitrión ubica a quien lleva su carro — y esa
 * exigencia va en la puerta de la reserva, no en la de la app.
 */
export function getContactVerificationRequirement(
  user: AuthContactUser | null | undefined,
  profile: Pick<UserProfile, 'email'> | null | undefined,
): ContactVerificationRequirement {
  if (hasVerifiedAuthPhone(user) || hasVerifiedAuthEmail(user, profile)) return null

  // Sin ninguno verificado: se manda a terminar la vía que la persona empezó.
  // Si escribió un correo real y no lo confirmó, es el correo; si no, el
  // teléfono, que es la vía de quien llegó por SMS o por Google.
  return realContactEmail(user, profile) ? 'email' : 'phone'
}

/**
 * Lo que queda PENDIENTE sin detener a nadie.
 *
 * Se muestra como un paso más del centro de verificación, junto al DUI, la
 * licencia y la selfie. La exigencia no se perdió: se pide donde la persona
 * puede atenderla cuando quiera, en vez de como un peaje entre ella y la app
 * que acaba de instalar.
 *
 * Devuelve los dos casos porque el hueco puede estar de cualquier lado: quien
 * entró por teléfono no tiene correo, y quien entró por correo no tiene
 * teléfono.
 */
export function getPendingContactVerification(
  user: AuthContactUser | null | undefined,
  profile: Pick<UserProfile, 'email'> | null | undefined,
): ContactVerificationRequirement {
  if (!hasVerifiedAuthEmail(user, profile)) return 'email'
  if (!hasVerifiedAuthPhone(user)) return 'phone'
  return null
}
